'use client';

import { useState, useEffect } from 'react';
import {
  Box,
  Stack,
  Text,
  Flex,
  HStack,
  Button,
  Input,
  Icon,
  SimpleGrid,
  InputGroup,
  InputLeftElement,
} from '@chakra-ui/react';
import { MdSearch } from 'react-icons/md';
import { FeatureType } from '@habity/types';
import { FEATURES } from '@habity/constants';
import { getFeatureButtonIcon } from '~/constants';
import { EditButton } from './EditButton';

interface FeatureListEditorProps {
  /**
   * Current list of selected features
   */
  features: FeatureType[];

  /**
   * Function called when features are saved
   */
  onSave: (features: FeatureType[]) => Promise<void> | void;

  /**
   * Whether the user can edit the features
   */
  canEdit?: boolean;

  /**
   * Title for the features section
   */
  title?: string;

  /**
   * List of benefits
   */
  benefits?: string[];
}

/**
 * A specialized editor component for managing feature lists
 */
export const FeatureListEditor = ({
  features,
  onSave,
  canEdit = true,
  title = 'Det bästa med bostaden',
  benefits,
}: FeatureListEditorProps) => {
  const [isEditing, setIsEditing] = useState(false);
  const [featuresState, setFeaturesState] = useState<FeatureType[]>(
    features || []
  );
  const [searchQuery, setSearchQuery] = useState('');
  const [isUpdating, setIsUpdating] = useState(false);
  const isMobile = typeof window !== 'undefined' && window.innerWidth < 768;

  // Get all available features that aren't hidden
  const allFeatures = FEATURES.filter((f) => !f.hidden);

  // Filter features based on search query
  const filteredFeatures = allFeatures
    .filter(
      (f) =>
        searchQuery === '' ||
        f.title.toLowerCase().includes(searchQuery.toLowerCase())
    )
    .sort((a, b) => a.title.localeCompare(b.title, 'sv'));

  // Get selected features with their full data
  const selectedFeatures = FEATURES.filter((f) =>
    featuresState?.includes(f.key as FeatureType)
  );

  // Reset state when features prop changes
  useEffect(() => {
    setFeaturesState(features || []);
  }, [features]);

  // Handle keyboard shortcuts for save and cancel
  useEffect(() => {
    if (!isEditing) return;

    const handleGlobalKeyDown = (event: KeyboardEvent) => {
      // Save on Enter key (without modifiers) or Ctrl+Enter
      if (
        event.key === 'Enter' &&
        !event.shiftKey &&
        // Don't trigger if the event came from an input element
        !(event.target instanceof HTMLInputElement)
      ) {
        event.preventDefault();
        saveFeatures();
      } else if (event.key === 'Escape') {
        event.preventDefault();
        cancelEditing();
      }
    };

    document.addEventListener('keydown', handleGlobalKeyDown);
    return () => {
      document.removeEventListener('keydown', handleGlobalKeyDown);
    };
  }, [isEditing]);

  const toggleFeature = (feature: FeatureType) => {
    if (featuresState?.includes(feature)) {
      // Remove feature if already selected
      const newFeatures: FeatureType[] = featuresState.filter(
        (f) => f !== feature
      );
      setFeaturesState(newFeatures);
    } else {
      // Add feature if not already selected
      const newFeatures: FeatureType[] = [...(featuresState || []), feature];
      setFeaturesState(newFeatures);
    }
  };

  const saveFeatures = async () => {
    if (!onSave) return;

    try {
      setIsUpdating(true);
      await onSave(featuresState);
      setIsEditing(false);
    } catch (error) {
      console.error('Failed to save features:', error);
    } finally {
      setIsUpdating(false);
    }
  };

  const cancelEditing = () => {
    setFeaturesState(features || []);
    setIsEditing(false);
    setSearchQuery('');
  };

  const startEditing = () => {
    setIsEditing(true);
  };

  return (
    <Stack
      spacing="4"
      pb={{ mobile: 2, desktop: 10 }}
      pt={{ mobile: 0, desktop: 6 }}
      // borderTopWidth="1px"
      // borderTopColor="rgba(0,0,0,0.2)"
    >
      <HStack justify="space-between">
        <Text fontWeight="light" size="sm">
          {title}
        </Text>
        {canEdit && !isEditing && <EditButton onClick={startEditing} />}
      </HStack>

      {isEditing ? (
        <Stack spacing="6">
          {/* Search input */}
          <Box width={{ base: 'full', md: '300px' }}>
            <InputGroup>
              <InputLeftElement pointerEvents="none">
                <Icon as={MdSearch} boxSize="5" color="black" />
              </InputLeftElement>
              <Input
                placeholder="Sök fördelar..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                size="md"
                borderColor="black"
                borderRadius="5"
                _hover={{ borderColor: 'black' }}
                _focus={{ borderColor: 'black', boxShadow: '0 0 0 1px black' }}
              />
            </InputGroup>
          </Box>

          {/* All features in a single list */}
          <SimpleGrid columns={{ base: 1, sm: 2, md: 3 }} spacing="3">
            {filteredFeatures.map((feature) => {
              const isSelected = featuresState?.includes(
                feature.key as FeatureType
              );
              return (
                <HStack
                  key={feature.key}
                  px="3"
                  py="2"
                  bg={isSelected ? 'brand.900' : 'brand.50'}
                  borderRadius="md"
                  spacing="2"
                  cursor="pointer"
                  onClick={() => toggleFeature(feature.key as FeatureType)}
                  borderWidth="1px"
                  borderColor={isSelected ? 'brand.900' : 'brand.200'}
                  _hover={{ bg: isSelected ? 'brand.900' : 'brand.200' }}
                >
                  <Icon
                    as={getFeatureButtonIcon(feature.key as FeatureType)}
                    boxSize="4"
                    color={isSelected ? 'white' : 'brand.600'}
                  />
                  <Text color={isSelected ? 'white' : 'brand.600'}>
                    {feature.title}
                  </Text>
                </HStack>
              );
            })}
          </SimpleGrid>

          <HStack spacing="2" mt="4">
            <Button
              colorScheme="brand"
              size="md"
              onClick={saveFeatures}
              isLoading={isUpdating}
            >
              Spara
            </Button>
            <Button
              variant="outline"
              size="md"
              onClick={cancelEditing}
              isDisabled={isUpdating}
            >
              Avbryt
            </Button>
          </HStack>
        </Stack>
      ) : (
        <Flex flexWrap="wrap" gap="4">
          {selectedFeatures.map((feature, index) => (
            <Box key={index}>
              <Stack
                px="3"
                py="1"
                rounded="full"
                backgroundColor="brand.900"
                color="white"
              >
                <HStack spacing="2" py="1">
                  <Icon
                    as={getFeatureButtonIcon(feature.key as FeatureType)}
                    boxSize="14px"
                  />
                  <Text
                    size="sm"
                    whiteSpace={isMobile ? 'pre-line' : 'inherit'}
                  >
                    {feature.title}
                  </Text>
                </HStack>
              </Stack>
            </Box>
          ))}
        </Flex>
      )}
    </Stack>
  );
};
